import React, { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import AuthenticatedRoute from './AuthenticatedRoute'
import { selectInStore } from './features/app/selectors'
import { selectUsername } from './features/auth/selectors'
import azureCommunicationActions from './features/azureCommunication/actions'
import panelsActions from './features/panels/actions'
import routerActions from './features/router/actions'
import usersActions from './features/users/actions'
import { selectUser } from './features/users/selectors'
import { useLogoutNavigation } from './hooks/navigation/useLogoutNavigation'
import { useTokenError } from './hooks/useTokenError'
import { useTeloNavigate } from './routing/teloNavigate'
import { useTeloDispatch, useTeloSelector } from './store'
import {
	LazyAdminInstrumentsInRoomPage,
	LazyAdminPage,
	LazyAdminStoreReferralDoctorPage,
	LazyAppointmentPage,
	LazyCallWidgetWrapper,
	LazyCheckInPageV2,
	LazyEclipsLoginPage,
	LazyEclipsTokenCodePage,
	LazyExamAttributionPage,
	LazyExamPage,
	LazyExamSearchedPage,
	LazyGrantsPage,
	LazyLoginPage,
	LazyLogoutPage,
	LazyNewAppointmentPage,
	// LazyNewOrEditDiangosiPlanPage,
	LazyNewOrEditPanelPage,
	LazyNewStoreAdminPage,
	LazyNewUserPage,
	LazyPatientAlertsPage,
	LazyPatientList,
	LazyPostLogoutPage,
	LazyPracticeAdminPage,
	LazyProfilePage,
	LazyRefractionistDashboardPage,
	LazyRefractionistExamPage,
	LazyRenewalDashboardPage,
	LazyRenewalSingleTestPage,
	LazyRescheduleAppointmentPage,
	LazyRoomDeviceSelectionPage,
	LazySearchPage,
	LazyStoreAdminPage,
	LazyStoreSelectionPage,
	LazyTokenCodePage,
	LazyUserAdminPage,
	LazyWorklistPage,
} from './routing/lazyRoutes'

const TeloRoutes = () => {
	const dispatch = useTeloDispatch()
	const navigate = useTeloNavigate()
	const username = useTeloSelector(selectUsername)
	const user = useTeloSelector(selectUser)
	const inStore = useTeloSelector(selectInStore)
	const shouldIFetchUser = user === undefined
	const eclipsRoute = window.location.href.indexOf('eclips') !== -1

	const location = useLocation()
	useEffect(() => {
		dispatch(routerActions.setLocation(location))
	}, [location, dispatch])

	useEffect(() => {
		if (!username || eclipsRoute === true) {
			return
		}

		if (shouldIFetchUser === true) {
			dispatch(usersActions.fetchUser(username, navigate)).then(user => {
				if (user && user.loggedInAsInStore !== inStore) {
					dispatch(
						usersActions.updateUserInStoreFlag({
							_id: user._id,
							loggedInAsInStore: inStore,
						}),
					)
				}
				if (user?.role === 'Doctor') {
					dispatch(panelsActions.getDoctorPanels())
				}
				if (user && (user.role === 'Doctor' || user.role === 'Refractionist')) {
					dispatch(azureCommunicationActions.getAzureCommunicationToken())
				}
			})
		}
	}, [dispatch, username, inStore, shouldIFetchUser, eclipsRoute, navigate])

	useLogoutNavigation()

	if (useTokenError()) {
		return (
			<LazyLoginPage errorMessage="Session expired, you need to login again" />
		)
	}

	return (
		<Routes>
			<Route path="/eclips/init/:username" element={<LazyEclipsLoginPage />} />
			<Route path="/eclips/app-code" element={<LazyEclipsTokenCodePage />} />

			{/* DEPRECATED */}
			<Route
				path="/exam-attribution/:examId"
				element={
					<AuthenticatedRoute>
						<LazyExamAttributionPage />
					</AuthenticatedRoute>
				}
			/>

			<Route
				path="/store/:storeId/patients/:internalPatientId/exams/:examId/current/:currentExamId/:step"
				element={
					<AuthenticatedRoute>
						<LazyExamPage />
						<LazyCallWidgetWrapper />
					</AuthenticatedRoute>
				}
			/>

			<Route
				path="/store/:storeId/patients/:internalPatientId/exams/:examId/:step"
				element={
					<AuthenticatedRoute>
						<LazyExamPage />
						<LazyCallWidgetWrapper />
					</AuthenticatedRoute>
				}
			/>

			<Route
				path="/store/:storeId/appointment/:appointmentId"
				element={
					<AuthenticatedRoute>
						<LazyAppointmentPage />
					</AuthenticatedRoute>
				}
			/>

			<Route
				path="/store/:storeId/check-in/exam/:examId/patient/:internalPatientId"
				element={
					<AuthenticatedRoute>
						<LazyCheckInPageV2 />
					</AuthenticatedRoute>
				}
			/>

			<Route path="/login" element={<LazyLoginPage />} />
			<Route path="/logout" element={<LazyLogoutPage />} />
			<Route path="/postlogout" element={<LazyPostLogoutPage />} />
			<Route
				path="/store/:storeId/patient-history/:internalPatientId"
				element={
					<AuthenticatedRoute>
						<LazyExamSearchedPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/room-device"
				element={
					<AuthenticatedRoute>
						<LazyRoomDeviceSelectionPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/store-selection"
				element={
					<AuthenticatedRoute>
						<LazyStoreSelectionPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/store/:storeId/worklist"
				element={
					<AuthenticatedRoute>
						<LazyWorklistPage />
					</AuthenticatedRoute>
				}
			/>

			<Route
				path="/store/:storeId/search"
				element={
					<AuthenticatedRoute>
						<LazySearchPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/store/:storeId/patient-list"
				element={
					<AuthenticatedRoute>
						<LazyPatientList />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/refractionist/exam/:examId"
				element={
					<AuthenticatedRoute>
						<LazyRefractionistExamPage />
						<LazyCallWidgetWrapper />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/store/:storeId/new-appointment"
				element={
					<AuthenticatedRoute>
						<LazyNewAppointmentPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/store/:storeId/appointment/:appointmentId/edit"
				element={
					<AuthenticatedRoute>
						<LazyRescheduleAppointmentPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/doctor/exam/:examId/current/:currentExamId"
				element={
					<AuthenticatedRoute>
						<LazyExamPage />
						<LazyCallWidgetWrapper />
					</AuthenticatedRoute>
				}
			/>

			<Route
				path="/doctor/exam/:examId"
				element={
					<AuthenticatedRoute>
						<LazyExamPage />
						<LazyCallWidgetWrapper />
					</AuthenticatedRoute>
				}
			/>

			<Route
				path="/refractionist/dashboard"
				element={
					<AuthenticatedRoute>
						<LazyRefractionistDashboardPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/doctor/worklist"
				element={
					<AuthenticatedRoute>
						<LazyWorklistPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/doctor/renewal/dashboard"
				element={
					<AuthenticatedRoute>
						<LazyRenewalDashboardPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/doctor/search"
				element={
					<AuthenticatedRoute>
						<LazySearchPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/doctor/renewal/test/:examId"
				element={
					<AuthenticatedRoute>
						<LazyRenewalSingleTestPage />
						<LazyCallWidgetWrapper />
					</AuthenticatedRoute>
				}
			/>

			<Route path="/app-code" element={<LazyTokenCodePage />} />
			<Route path="/grants" element={<LazyGrantsPage />} />
			<Route
				path="/admin/store/:storeId/settings/stage/:stageId/room/:roomId"
				element={
					<AuthenticatedRoute>
						<LazyAdminInstrumentsInRoomPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/store/:storeId"
				element={
					<AuthenticatedRoute>
						<LazyWorklistPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin"
				element={
					<AuthenticatedRoute>
						<LazyAdminPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/new-store"
				element={
					<AuthenticatedRoute>
						<LazyNewStoreAdminPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/new-panel"
				element={
					<AuthenticatedRoute>
						<LazyNewOrEditPanelPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/panel/:panelId"
				element={
					<AuthenticatedRoute>
						<LazyNewOrEditPanelPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/new-practice"
				element={
					<AuthenticatedRoute>
						<LazyPracticeAdminPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/practice/:practiceId"
				element={
					<AuthenticatedRoute>
						<LazyPracticeAdminPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/user/:username"
				element={
					<AuthenticatedRoute>
						<LazyUserAdminPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/new-user"
				element={
					<AuthenticatedRoute>
						<LazyNewUserPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/store/:storeId/referral-doctor/:referralDoctorId"
				element={
					<AuthenticatedRoute>
						<LazyAdminStoreReferralDoctorPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/store/:storeId/new-referral-doctor"
				element={
					<AuthenticatedRoute>
						<LazyAdminStoreReferralDoctorPage isCreate={true} />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/store/:storeId"
				element={
					<AuthenticatedRoute>
						<LazyStoreAdminPage />
					</AuthenticatedRoute>
				}
			/>
			{/*<Route
				path="/admin/new-plan"
				element={
					<AuthenticatedRoute>
						<LazyNewOrEditDiangosiPlanPage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/admin/plan/:planId"
				element={
					<AuthenticatedRoute>
						<LazyNewOrEditDiangosiPlanPage />
					</AuthenticatedRoute>
				}
			/>*/}
			<Route
				path="/profile"
				element={
					<AuthenticatedRoute>
						<LazyProfilePage />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/doctor/exam/:examId/patient/:patientId/alerts"
				element={
					<AuthenticatedRoute>
						<LazyPatientAlertsPage />
						<LazyCallWidgetWrapper />
					</AuthenticatedRoute>
				}
			/>
			<Route
				path="/doctor/exam/:examId/current/:currentExamId/patient/:patientId/alerts"
				element={
					<AuthenticatedRoute>
						<LazyPatientAlertsPage />
						<LazyCallWidgetWrapper />
					</AuthenticatedRoute>
				}
			/>

			<Route path="/" element={<Navigate to="/login" />} />
		</Routes>
	)
}

export default TeloRoutes
